import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Button,
  Divider,
  Alert,
  Skeleton,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import PasswordChangeDialog from '../components/PasswordChangeDialog';
import ProfilePictureUpload from '../components/ProfilePictureUpload';
import EmployeeProjectHistory from '../components/employees/EmployeeProjectHistory';
import { useQueryClient } from '@tanstack/react-query';
import { useProfile } from '../hooks/useProfile';
import { useTechnologyLabels } from '../hooks/useTechnologies';
import { formatDate } from '../lib/formatDate';

const Profile: React.FC = () => {
  const { user, updateUserProfilePicture } = useAuth();
  const queryClient = useQueryClient();
  const { employee, isLoading: loading, error } = useProfile();
  const { data: technologyLabels = {} } = useTechnologyLabels();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  if (loading) {
    return (
      <Box sx={{ maxWidth: 1000, mx: 'auto', p: 2 }}>
        <Card sx={{ bgcolor: 'background.paper', boxShadow: 6 }}>
          <CardHeader title={<Skeleton variant="text" width={200} height={40} />} />
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
              <Box sx={{ flex: { md: '0 0 300px' }, textAlign: 'center' }}>
                <Skeleton variant="circular" width={120} height={120} sx={{ mx: 'auto', mb: 2 }} />
                <Skeleton variant="text" width={150} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="100%" height={30} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="80%" height={30} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="60%" height={30} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="90%" height={30} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 2 }}>
      <Card sx={{ bgcolor: 'background.paper', boxShadow: 6, p: 2 }}>
        <CardHeader 
          title={<Typography variant="h5" fontWeight={700}>Profile</Typography>}
          action={
            <Button
              variant="outlined"
              startIcon={<LockIcon />}
              onClick={() => setPasswordDialogOpen(true)}
            >
              Change Password
            </Button>
          }
        />
        <CardContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{(error as any)?.message || 'Failed to load profile'}</Alert>}
          
          {employee && (
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
              <Box sx={{ flex: { md: '0 0 300px' }, textAlign: 'center' }}>
                <ProfilePictureUpload
                  currentImageUrl={user?.profilePicture || employee?.profilePicture || employee?.user?.profilePicture}
                  entityType="USER"
                  entityId={user?.id || ''}
                  onImageChange={(imageUrl) => {
                    // Update the AuthContext user data
                    updateUserProfilePicture(imageUrl);
                    // Invalidate queries to refresh data
                    queryClient.invalidateQueries({ queryKey: ['profile'] });
                    queryClient.invalidateQueries({ queryKey: ['employees'] });
                  }}
                />
                <Typography variant="h6" fontWeight={600} sx={{ mt: 2 }}>
                  {employee.firstName} {employee.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {employee.position || 'Employee'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Personal Information
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: { sm: '1 1 200px' } }}>
                    <Typography variant="body2" color="text.secondary">
                      First Name
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {employee.firstName}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ flex: { sm: '1 1 200px' } }}>
                    <Typography variant="body2" color="text.secondary">
                      Last Name
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {employee.lastName}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ flex: { sm: '1 1 200px' } }}>
                    <Typography variant="body2" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {employee.phone || 'Not provided'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ flex: { sm: '1 1 200px' } }}>
                    <Typography variant="body2" color="text.secondary">
                      Emergency Contact
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {employee.emergencyContact || 'Not provided'}
                    </Typography>
                  </Box>

                  <Box sx={{ flex: { sm: '1 1 200px' } }}>
                    <Typography variant="body2" color="text.secondary">
                      Birth Date
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {formatDate(employee.birthDate)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ flex: { sm: '1 1 200px' } }}>
                    <Typography variant="body2" color="text.secondary">
                      Hire Date
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {formatDate(employee.hireDate)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ flex: '1 1 200px' }}>
                    <Typography variant="body2" color="text.secondary">
                      Address
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {employee.address || 'Not provided'}
                    </Typography>
                  </Box>
                  
                  {employee.salary && (
                    <Box sx={{ flex: { sm: '1 1 200px' } }}>
                      <Typography variant="body2" color="text.secondary">
                        Salary
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        €{employee.salary.toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {employee.technologies && employee.technologies.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Technologies
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {employee.technologies.map((tech: string) => (
                        <Typography
                          key={tech}
                          variant="body2"
                          sx={{
                            bgcolor: 'background.paper',
                            color: 'white',
                            px: 2,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                          }}
                        >
                          {technologyLabels[tech] || tech}
                        </Typography>
                      ))}
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Project History */}
      {employee && <EmployeeProjectHistory employee={employee} />}

      <PasswordChangeDialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        isAdmin={false}
      />
    </Box>
  );
};

export default Profile; 